import { Head, router, usePage } from '@inertiajs/react'
import { format, parseISO } from 'date-fns'
import { GitCommitIcon, UserIcon } from 'lucide-react'

import { RelativeTime } from '@/components/changelog/relative-time'
import { PublicViewSettings } from '@/components/organizations/public-view-settings'
import { TrendsDrawer } from '@/components/trends/trends-drawer'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { DashboardShell } from '@/layouts/dashboard-shell'
import { ORDERED_COMMIT_TAGS, resolveCommitTag } from '@/lib/commit-tags'
import { organizationRepositoryPath } from '@/routes'
import type { OrganizationsRepositoriesShow } from '@/types'

export default function OrganizationRepositoryShow({
  organization,
  repositories,
  repository,
  commits,
  fromDate,
  toDate,
  trends,
  publicChangelogMutationsEnabled,
}: OrganizationsRepositoriesShow) {
  const { url } = usePage()
  const query = new URLSearchParams(url.includes('?') ? url.split('?')[1] : '')
  const activeTag = query.get('tag') ?? undefined
  const activeAuthor = query.get('author') ?? undefined

  const navigateWithFilters = (next: { from?: string; to?: string; tag?: string | null; author?: string | null }) => {
    const tag = next.tag === undefined ? activeTag : next.tag || undefined
    const author = next.author === undefined ? activeAuthor : next.author || undefined

    router.get(
      organizationRepositoryPath(organization.id, repository.owner, repository.name, {
        from: next.from ?? fromDate,
        to: next.to ?? toDate,
        ...(tag ? { tag } : {}),
        ...(author ? { author } : {}),
      }),
      {},
      { preserveState: true, preserveScroll: true },
    )
  }

  const filterByTag = (tag: string) => {
    navigateWithFilters({
      tag: activeTag?.toLowerCase() === tag.toLowerCase() ? null : tag.toLowerCase(),
    })
  }

  const filterByAuthor = (author: string) => {
    navigateWithFilters({
      author: activeAuthor === author ? null : author,
    })
  }

  return (
    <>
      <Head title={`${repository.fullName} — ${organization.name}`} />
      <DashboardShell organization={organization} repositories={repositories} activeRepositoryId={repository.id}>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight">{repository.name}</h1>
              <p className="text-muted-foreground text-sm">{repository.fullName}</p>
            </div>
            <TrendsDrawer trends={trends} />
            <PublicViewSettings
              organizationId={organization.id}
              isPublicView={repository.isPublicView ?? false}
              owner={repository.owner}
              repo={repository.name}
              mutationsEnabled={publicChangelogMutationsEnabled}
            />
          </div>
          <DateRangePicker
            from={parseISO(fromDate)}
            to={parseISO(toDate)}
            onChange={({ from, to }) =>
              navigateWithFilters({
                from: format(from, 'yyyy-MM-dd'),
                to: format(to, 'yyyy-MM-dd'),
              })
            }
          />
        </div>

        <div className="bg-card text-card-foreground mx-auto w-full max-w-3xl rounded-lg border p-4 shadow-sm sm:p-6">
          {commits.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No digest available for the selected date range.
            </p>
          ) : (
            <div className="space-y-4">
              {commits.map((commit) => {
                const knownTags = ORDERED_COMMIT_TAGS.filter((tag) =>
                  (commit.tags ?? []).some((value) => value.toLowerCase() === tag.toLowerCase()),
                )
                const extraTags = (commit.tags ?? []).filter(
                  (value) => !ORDERED_COMMIT_TAGS.some((tag) => tag.toLowerCase() === value.toLowerCase()),
                )
                const createdAt = commit.createdAt || commit.isoDate

                return (
                  <div key={commit.id} className="bg-muted rounded-lg p-4 shadow-xs">
                    <div className="mb-2 flex items-start justify-between gap-4">
                      <div className="flex flex-wrap items-center gap-2">
                        {[...knownTags, ...extraTags].map((tag) => {
                          const resolved = resolveCommitTag(tag)
                          return (
                            <Badge
                              key={`${commit.id}-${resolved.label}`}
                              variant="outline"
                              role="button"
                              tabIndex={0}
                              onClick={() => filterByTag(resolved.label)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  filterByTag(resolved.label)
                                }
                              }}
                              className={`flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-xs select-none ${resolved.color}`}
                            >
                              {resolved.emoji ? `${resolved.emoji} ` : null}
                              {resolved.label}
                            </Badge>
                          )
                        })}
                      </div>
                      {createdAt ? (
                        <RelativeTime
                          timestamp={createdAt}
                          className="text-muted-foreground mt-0 min-w-20 shrink-0 text-right text-xs"
                        />
                      ) : null}
                    </div>

                    <div className="text-foreground mb-2 text-sm whitespace-pre-wrap">{commit.summary}</div>

                    <div className="text-muted-foreground flex flex-col gap-2 text-xs md:flex-row md:items-start md:justify-between md:gap-4">
                      <div className="flex flex-wrap items-center gap-4">
                        <button
                          type="button"
                          className="hover:decoration-golden-500 flex cursor-pointer items-center select-none hover:underline"
                          onClick={() => filterByAuthor(commit.author)}
                        >
                          <Avatar className="mr-1 size-4">
                            {commit.authorAvatarUrl ? (
                              <AvatarImage src={commit.authorAvatarUrl} alt={commit.author} />
                            ) : null}
                            <AvatarFallback>
                              <UserIcon className="size-3" />
                            </AvatarFallback>
                          </Avatar>
                          {commit.author}
                        </button>

                        <a
                          href={commit.htmlUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:decoration-golden-500 flex items-start hover:underline"
                        >
                          <GitCommitIcon className="mr-1 size-4 shrink-0" />
                          <span className="line-clamp-2 max-w-full md:max-w-xs">
                            {commit.pullRequestNumber
                              ? `${commit.pullRequestTitle || commit.message} (#${commit.pullRequestNumber})`
                              : commit.message || commit.sha.slice(0, 7)}
                          </span>
                        </a>
                      </div>

                      <div className="flex items-center gap-2 font-mono">
                        <span className="text-green-700" title="Lines Added">
                          +{commit.linesAdded.toLocaleString()}
                        </span>
                        <span className="text-red-600" title="Lines Removed">
                          -{commit.linesRemoved.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </DashboardShell>
    </>
  )
}
